import { useEffect, useMemo, useState } from "react";
import client from "../api/client";
import { getNextVoucherNo } from "../services/voucherNumberService";
import { useAuth } from "../context/AuthContext";
import { useFinancialYear } from "../context/FinancialYearContext";

const today = () => new Date().toISOString().split("T")[0];

const REFERENCE_TYPES = [
  {
    value: "ON_ACCOUNT",
    label: "On A/C",
    help: "General payment without bill adjustment",
  },
  {
    value: "TOWARDS_BILL",
    label: "Against Bill / Reference",
    help: "Supplier payment against bill/reference",
  },
  {
    value: "ADVANCE",
    label: "Advance",
    help: "Advance paid before purchase bill",
  },
  {
    value: "NEW_BILL",
    label: "New Bill",
    help: "Payment linked to a new manual bill reference",
  },
];

const initialForm = {
  voucherNo: "",
  voucherDate: today(),
  paymentMode: "CASH",
  referenceType: "ON_ACCOUNT",
  billReferenceNo: "",
  supplierId: "",
  debitAccountId: "",
  creditAccountId: "",
  amount: "",
  notes: "",
};

const getId = (x) => x?.Id ?? x?.id ?? x?.AccountId ?? x?.accountId ?? "";
const getAccountCode = (x) =>
  (x?.AccountCode ?? x?.accountCode ?? "").toString();
const getAccountName = (x) =>
  (x?.AccountName ?? x?.accountName ?? "").toString();
const getAccountType = (x) =>
  (x?.AccountType ?? x?.accountType ?? "").toString();
const getParentId = (x) =>
  (
    x?.ParentAccountId ??
    x?.parentAccountId ??
    x?.ParentId ??
    x?.parentId ??
    ""
  ).toString();
const getParentCode = (x) =>
  (
    x?.ParentAccountCode ??
    x?.parentAccountCode ??
    x?.ParentCode ??
    x?.parentCode ??
    ""
  ).toString();
const getParentName = (x) =>
  (
    x?.ParentAccountName ??
    x?.parentAccountName ??
    x?.ParentName ??
    x?.parentName ??
    ""
  ).toString();

const getSupplierId = (x) =>
  x?.Id ?? x?.id ?? x?.SupplierId ?? x?.supplierId ?? "";
const getSupplierName = (x) =>
  x?.SupplierName ?? x?.supplierName ?? x?.Name ?? x?.name ?? "";
const getSupplierCode = (x) =>
  x?.SupplierCode ?? x?.supplierCode ?? x?.Code ?? x?.code ?? "";
const getSupplierAccountId = (x) =>
  x?.AccountId ??
  x?.accountId ??
  x?.LedgerAccountId ??
  x?.ledgerAccountId ??
  "";

const isParentControlLedger = (x) => {
  const code = getAccountCode(x);
  const name = getAccountName(x).toLowerCase();

  return (
    code === "1300" ||
    code === "2100" ||
    name === "accounts receivable a/c" ||
    name === "accounts payable a/c" ||
    name === "accounts receivable" ||
    name === "accounts payable"
  );
};

const isGstLedger = (x) =>
  /gst|cgst|sgst|igst/i.test(`${getAccountCode(x)} ${getAccountName(x)}`);

const isCashBankLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const code = getAccountCode(x);

  return (
    name.includes("cash") ||
    name.includes("bank") ||
    name.includes("upi") ||
    code === "1100" ||
    code === "1200"
  );
};

const isPayableChildLedger = (x) => {
  if (isParentControlLedger(x)) return false;

  const code = getAccountCode(x);
  const name = getAccountName(x).toLowerCase();
  const parentId = getParentId(x);
  const parentCode = getParentCode(x);
  const parentName = getParentName(x).toLowerCase();

  return (
    (code.startsWith("210") && code !== "2100") ||
    parentId === "2100" ||
    parentCode === "2100" ||
    parentName.includes("accounts payable") ||
    parentName.includes("sundry creditor") ||
    parentName.includes("creditor") ||
    name.includes("supplier") ||
    name.includes("creditor")
  );
};

const isExpenseLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();

  return (
    type === "expense" ||
    name.includes("expense") ||
    name.includes("rent") ||
    name.includes("salary") ||
    name.includes("electricity") ||
    name.includes("wages")
  );
};

const isPurchaseLedger = (x) => {
  const name = getAccountName(x).toLowerCase();
  return name.includes("purchase") || name.includes("inventory");
};

const isLiabilityLedger = (x) => {
  if (isParentControlLedger(x)) return false;

  const name = getAccountName(x).toLowerCase();
  const type = getAccountType(x).toLowerCase();

  return (
    type === "liability" ||
    name.includes("loan") ||
    name.includes("gst payable") ||
    name.includes("advance")
  );
};

const money = (v) =>
  Number(v || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export default function PaymentVoucherPage() {
  const { user } = useAuth();
  const { financialYear } = useFinancialYear();

  const companyId = Number(
    user?.companyId || localStorage.getItem("companyId") || 0,
  );
  const fyFrom = financialYear?.fromDate;
  const fyTo = financialYear?.toDate;
  const fyLocked = Boolean(financialYear?.isLocked || financialYear?.IsLocked);

  const [form, setForm] = useState({
    ...initialForm,
    voucherDate: fyFrom || today(),
  });

  const [accounts, setAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoNarration, setAutoNarration] = useState(true);
  const [dateLocked, setDateLocked] = useState(false);
  const [checkingDateLock, setCheckingDateLock] = useState(false);

  const selectedDateOutsideFy =
    form.voucherDate && fyFrom && fyTo
      ? form.voucherDate < fyFrom || form.voucherDate > fyTo
      : false;

  const isPostingBlocked =
    fyLocked || dateLocked || selectedDateOutsideFy || checkingDateLock;

  const findAccount = (mode, list = accounts) => {
    const m = (mode || "").toLowerCase();

    if (m === "cash") {
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("cash") ||
          getAccountCode(x) === "1100",
      );
    }

    if (m === "bank") {
      return list.find(
        (x) =>
          getAccountName(x).toLowerCase().includes("bank") ||
          getAccountCode(x) === "1200",
      );
    }

    if (m === "upi") {
      return (
        list.find((x) => getAccountName(x).toLowerCase().includes("upi")) ||
        list.find((x) => getAccountName(x).toLowerCase().includes("bank"))
      );
    }

    return list.find((x) => getAccountType(x).toLowerCase() === "asset");
  };

  const loadData = async () => {
    try {
      const [accRes, supRes] = await Promise.all([
        client.get("/accounts"),
        client.get("/suppliers"),
      ]);

      const acc = Array.isArray(accRes.data) ? accRes.data : [];
      const sup = Array.isArray(supRes.data) ? supRes.data : [];
      const cash = findAccount("CASH", acc);
      const nextNo = await getNextVoucherNo("payment", fyFrom || today());

      setAccounts(acc);
      setSuppliers(sup);

      setForm((prev) => ({
        ...prev,
        voucherDate: fyFrom || prev.voucherDate,
        voucherNo: prev.voucherNo || nextNo,
        creditAccountId: getId(cash) || "",
      }));
    } catch (err) {
      console.error(err);
      alert("Failed to load accounts/suppliers");
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (fyFrom) {
      setForm((p) => ({
        ...p,
        voucherDate: fyFrom,
        voucherNo: "",
      }));
    }
  }, [fyFrom]);

  const checkVoucherDateLock = async (voucherDate) => {
    if (!voucherDate || !companyId) {
      setDateLocked(false);
      return;
    }

    try {
      setCheckingDateLock(true);

      const res = await client.get("/finance/is-date-locked", {
        params: {
          entryDate: voucherDate,
          companyId,
        },
      });

      setDateLocked(Boolean(res.data?.isLocked));
    } catch (err) {
      console.error("Date lock check failed:", err);
      setDateLocked(false);
    } finally {
      setCheckingDateLock(false);
    }
  };

  useEffect(() => {
    checkVoucherDateLock(form.voucherDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.voucherDate, companyId]);

  const accountMap = useMemo(() => {
    const map = {};
    accounts.forEach((a) => {
      map[String(getId(a))] = `${getAccountCode(a)} - ${getAccountName(a)}`;
    });
    return map;
  }, [accounts]);

  const selectedSupplier = useMemo(
    () =>
      suppliers.find(
        (s) => String(getSupplierId(s)) === String(form.supplierId),
      ),
    [suppliers, form.supplierId],
  );

  const supplierLedgerAccountId = useMemo(() => {
    const id = getSupplierAccountId(selectedSupplier);
    return id ? String(id) : "";
  }, [selectedSupplier]);

  const paymentDebitAccounts = useMemo(
    () =>
      accounts.filter((a) => {
        if (isCashBankLedger(a) || isParentControlLedger(a)) return false;

        if (form.referenceType === "TOWARDS_BILL")
          return isPayableChildLedger(a);
        if (form.referenceType === "ADVANCE")
          return isPayableChildLedger(a) || isLiabilityLedger(a);

        if (isGstLedger(a))
          return getAccountName(a).toLowerCase().includes("payable");

        return (
          isPayableChildLedger(a) ||
          isExpenseLedger(a) ||
          isPurchaseLedger(a) ||
          isLiabilityLedger(a)
        );
      }),
    [accounts, form.referenceType],
  );

  const recommendedDebitAccountId = useMemo(() => {
    if (supplierLedgerAccountId) {
      const exists = accounts.some(
        (a) => String(getId(a)) === String(supplierLedgerAccountId),
      );

      if (exists) return supplierLedgerAccountId;
    }

    const supplierName = getSupplierName(selectedSupplier).toLowerCase();

    const exactSupplier = supplierName
      ? paymentDebitAccounts.find((a) =>
          getAccountName(a).toLowerCase().includes(supplierName),
        )
      : null;

    const firstPayableChild = paymentDebitAccounts.find((a) =>
      isPayableChildLedger(a),
    );

    const expense = paymentDebitAccounts.find((a) => isExpenseLedger(a));

    return (
      getId(
        exactSupplier ||
          firstPayableChild ||
          expense ||
          paymentDebitAccounts[0],
      ) || ""
    );
  }, [
    accounts,
    paymentDebitAccounts,
    selectedSupplier,
    supplierLedgerAccountId,
  ]);

  const paymentCreditAccounts = useMemo(
    () => accounts.filter((a) => isCashBankLedger(a)),
    [accounts],
  );

  const shouldLockDebitAccount =
    form.referenceType === "TOWARDS_BILL" &&
    !!form.supplierId &&
    !!recommendedDebitAccountId;

  useEffect(() => {
    if (
      shouldLockDebitAccount ||
      !form.debitAccountId ||
      !paymentDebitAccounts.some(
        (a) => String(getId(a)) === String(form.debitAccountId),
      )
    ) {
      if (recommendedDebitAccountId) {
        setForm((prev) => ({
          ...prev,
          debitAccountId: recommendedDebitAccountId,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    form.referenceType,
    form.supplierId,
    recommendedDebitAccountId,
    shouldLockDebitAccount,
  ]);

  const selectedDebitHint =
    form.referenceType === "TOWARDS_BILL"
      ? "Select exact Supplier ledger only. Parent 2100 Accounts Payable and Cash/Bank ledgers are hidden."
      : form.referenceType === "ADVANCE"
        ? "Select Supplier Advance / Liability or exact Supplier ledger."
        : "Select Supplier / Expense / Liability ledger. Cash/Bank and parent control ledgers are hidden.";

  const debitName =
    accountMap[String(form.debitAccountId)] || "Supplier / Expense A/c";
  const creditName =
    accountMap[String(form.creditAccountId)] || "Cash / Bank A/c";

  const refreshVoucherNo = async () => {
    if (isPostingBlocked) return;

    const no = await getNextVoucherNo("payment", form.voucherDate);
    setForm((prev) => ({ ...prev, voucherNo: no }));
  };

  const buildNarration = (f = form) => {
    const refLabel =
      REFERENCE_TYPES.find((x) => x.value === f.referenceType)?.label ||
      "On A/C";

    const party = selectedSupplier
      ? getSupplierName(selectedSupplier)
      : "party";

    const ref = f.billReferenceNo?.trim()
      ? `, Ref: ${f.billReferenceNo.trim()}`
      : "";

    return `Being amount paid to ${party} by ${f.paymentMode}${ref} (${refLabel})`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (isPostingBlocked && name !== "voucherDate") return;

    if (name === "voucherDate") {
      setForm((prev) => ({
        ...prev,
        voucherDate: value,
        voucherNo: "",
      }));
      return;
    }

    if (name === "paymentMode") {
      const selectedAccount = findAccount(value);

      setForm((prev) => ({
        ...prev,
        paymentMode: value,
        creditAccountId: getId(selectedAccount) || "",
        notes: autoNarration ? "" : prev.notes,
      }));

      return;
    }

    if (name === "referenceType") {
      setForm((prev) => ({
        ...prev,
        referenceType: value,
        billReferenceNo:
          value === "ON_ACCOUNT" || value === "ADVANCE"
            ? ""
            : prev.billReferenceNo,
        debitAccountId: "",
        notes: autoNarration ? "" : prev.notes,
      }));

      return;
    }

    if (name === "supplierId") {
      setForm((prev) => ({
        ...prev,
        supplierId: value,
        debitAccountId: "",
        notes: autoNarration ? "" : prev.notes,
      }));

      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
      notes:
        autoNarration && name !== "notes"
          ? ""
          : name === "notes"
            ? value
            : prev.notes,
    }));
  };

  const clearForm = async () => {
    const defaultAccount = findAccount(form.paymentMode);
    const nextNo = await getNextVoucherNo("payment", fyFrom || today());

    setForm({
      ...initialForm,
      voucherNo: nextNo,
      voucherDate: fyFrom || today(),
      paymentMode: form.paymentMode,
      creditAccountId: getId(defaultAccount) || "",
    });
  };

  const validationErrors = useMemo(() => {
    const errors = [];

    if (fyLocked)
      errors.push(
        "Selected financial year is locked. Payment cannot be posted.",
      );
    if (dateLocked)
      errors.push("Selected voucher date belongs to locked financial year.");
    if (selectedDateOutsideFy)
      errors.push(`Voucher date must be between ${fyFrom} and ${fyTo}.`);
    if (!companyId) errors.push("Company is required. Please login again.");
    if (!form.voucherNo.trim()) errors.push("Voucher No is required");
    if (!form.debitAccountId) errors.push("Select Debit Account");
    if (!form.creditAccountId) errors.push("Select Credit Account");

    if (
      String(form.debitAccountId) === String(form.creditAccountId) &&
      form.debitAccountId
    ) {
      errors.push("Debit and Credit account cannot be same");
    }

    const debitAccount = accounts.find(
      (a) => String(getId(a)) === String(form.debitAccountId),
    );

    if (debitAccount && isParentControlLedger(debitAccount)) {
      errors.push(
        "Do not post directly to parent control account. Select exact supplier ledger.",
      );
    }

    if (
      form.referenceType === "TOWARDS_BILL" &&
      form.supplierId &&
      !form.debitAccountId
    ) {
      errors.push("Supplier ledger account is required");
    }

    if (
      (form.referenceType === "NEW_BILL" ||
        form.referenceType === "TOWARDS_BILL") &&
      !form.billReferenceNo.trim()
    ) {
      errors.push("Bill / Reference No is required");
    }

    if (!form.amount || Number(form.amount) <= 0) {
      errors.push("Enter valid amount");
    }

    return errors;
  }, [
    form,
    accounts,
    fyLocked,
    dateLocked,
    selectedDateOutsideFy,
    companyId,
    fyFrom,
    fyTo,
  ]);

  const save = async () => {
    if (checkingDateLock)
      return alert("Please wait. Checking financial year lock status.");
    if (validationErrors.length) return alert(validationErrors[0]);

    try {
      setSaving(true);

      await client.post("/vouchers/payment", {
        companyId,
        voucherNo: form.voucherNo.trim(),
        voucherDate: form.voucherDate,
        paymentMode: form.paymentMode,
        referenceType: form.referenceType,
        billReferenceNo: form.billReferenceNo.trim() || null,
        supplierId: form.supplierId ? Number(form.supplierId) : null,
        debitAccountId: Number(form.debitAccountId),
        creditAccountId: Number(form.creditAccountId),
        amount: Number(form.amount),
        notes: (form.notes || buildNarration()).trim(),
      });

      alert("Payment saved and journal posted");
      await clearForm();
    } catch (err) {
      console.error("Payment Save Error:", err);

      alert(
        err?.response?.data?.message ||
          err?.response?.data?.title ||
          err?.response?.data ||
          err?.message ||
          "Failed to save payment",
      );
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const shortcut = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        save();
      }

      if (e.altKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        clearForm();
      }
    };

    window.addEventListener("keydown", shortcut);
    return () => window.removeEventListener("keydown", shortcut);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, accounts, validationErrors, isPostingBlocked]);

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "600",
    marginBottom: "3px",
  };

  const inputStyle = {
    height: "30px",
    fontSize: "12px",
    borderRadius: "6px",
  };

  const buttonStyle = {
    border: "none",
    padding: "5px 14px",
    fontSize: "12px",
    borderRadius: "6px",
  };

  const effectiveNotes = form.notes || buildNarration();

  return (
    <div style={{ padding: "0px", fontSize: "13px" }}>
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          boxShadow: "0 3px 10px rgba(0,0,0,0.08)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "linear-gradient(90deg,#0b4f4a,#0f766e)",
            color: "#fff",
            padding: "10px 14px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: "15px", fontWeight: "700" }}>
              Payment Voucher
            </div>
            <div style={{ fontSize: "11px", opacity: 0.9 }}>
              Supplier/Expense/Liability Dr → Cash/Bank Cr
            </div>
          </div>

          <span
            style={{
              background: isPostingBlocked ? "#dc3545" : "#fff",
              color: isPostingBlocked ? "#fff" : "#dc3545",
              padding: "3px 8px",
              borderRadius: "6px",
              fontSize: "11px",
              fontWeight: "600",
              border: isPostingBlocked ? "1px solid #fff" : "none",
            }}
          >
            {checkingDateLock
              ? "Checking FY"
              : isPostingBlocked
                ? "Posting Blocked"
                : "Auto Journal"}
          </span>
        </div>

        <div style={{ padding: "12px" }}>
          {checkingDateLock && (
            <div className="alert alert-info py-2" style={{ fontSize: 11 }}>
              Checking financial year lock status...
            </div>
          )}

          {fyLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-lock-fill me-1"></i>
              Selected financial year is locked. Payment posting is blocked.
            </div>
          )}

          {dateLocked && (
            <div className="alert alert-danger py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-lock-fill me-1"></i>
              Selected voucher date belongs to locked financial year.
            </div>
          )}

          {selectedDateOutsideFy && (
            <div className="alert alert-warning py-2" style={{ fontSize: 11 }}>
              <i className="bi bi-exclamation-triangle me-1"></i>
              Voucher date must be between {fyFrom} and {fyTo}.
            </div>
          )}

          <div className="row g-2">
            <div className="col-md-3">
              <label style={labelStyle}>Voucher No</label>
              <input
                name="voucherNo"
                className="form-control"
                style={inputStyle}
                value={form.voucherNo}
                readOnly
              />

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm mt-1"
                style={{ fontSize: "11px", padding: "2px 8px" }}
                onClick={refreshVoucherNo}
                disabled={isPostingBlocked}
              >
                Refresh No
              </button>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Date</label>
              <input
                type="date"
                name="voucherDate"
                className="form-control"
                style={inputStyle}
                min={fyFrom || undefined}
                max={fyTo || undefined}
                value={form.voucherDate}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Payment Mode</label>
              <select
                name="paymentMode"
                className="form-select"
                style={inputStyle}
                value={form.paymentMode}
                onChange={handleChange}
                disabled={isPostingBlocked}
              >
                <option value="CASH">Cash</option>
                <option value="BANK">Bank</option>
                <option value="UPI">UPI</option>
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Reference Type</label>
              <select
                name="referenceType"
                className="form-select"
                style={inputStyle}
                value={form.referenceType}
                onChange={handleChange}
                disabled={isPostingBlocked}
              >
                {REFERENCE_TYPES.map((x) => (
                  <option key={x.value} value={x.value}>
                    {x.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Bill / Reference No</label>
              <input
                name="billReferenceNo"
                className="form-control"
                style={inputStyle}
                value={form.billReferenceNo}
                onChange={handleChange}
                disabled={
                  isPostingBlocked ||
                  form.referenceType === "ON_ACCOUNT" ||
                  form.referenceType === "ADVANCE"
                }
                placeholder={
                  form.referenceType === "TOWARDS_BILL"
                    ? "Bill / reference no"
                    : form.referenceType === "NEW_BILL"
                      ? "New bill no"
                      : "Not required"
                }
              />
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Supplier</label>
              <select
                name="supplierId"
                className="form-select"
                style={inputStyle}
                value={form.supplierId}
                onChange={handleChange}
                disabled={isPostingBlocked}
              >
                <option value="">Optional - Select Supplier</option>
                {suppliers.map((s) => (
                  <option key={getSupplierId(s)} value={getSupplierId(s)}>
                    {getSupplierCode(s)} - {getSupplierName(s)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>
                Debit Account - Supplier/Expense/Dr
              </label>

              <select
                name="debitAccountId"
                className="form-select"
                style={inputStyle}
                value={form.debitAccountId}
                onChange={handleChange}
                disabled={isPostingBlocked || shouldLockDebitAccount}
              >
                <option value="">Select Supplier / Payable / Expense</option>
                {paymentDebitAccounts.map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>

              <div
                style={{
                  fontSize: "10px",
                  color: "#dc3545",
                  marginTop: "3px",
                }}
              >
                {selectedDebitHint}
              </div>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Credit Account - Cash/Bank/Asset</label>

              <select
                name="creditAccountId"
                className="form-select"
                style={inputStyle}
                value={form.creditAccountId}
                onChange={handleChange}
                disabled={isPostingBlocked}
              >
                <option value="">Select Cash / Bank / UPI</option>
                {(paymentCreditAccounts.length
                  ? paymentCreditAccounts
                  : accounts
                ).map((a) => (
                  <option key={getId(a)} value={getId(a)}>
                    {getAccountCode(a)} - {getAccountName(a)}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-3">
              <label style={labelStyle}>Amount</label>
              <input
                type="number"
                name="amount"
                className="form-control"
                style={{ ...inputStyle, textAlign: "right" }}
                value={form.amount}
                onChange={handleChange}
                disabled={isPostingBlocked}
              />
            </div>

            <div className="col-md-9">
              <label style={labelStyle}>Notes</label>

              <input
                name="notes"
                className="form-control"
                style={inputStyle}
                value={effectiveNotes}
                disabled={isPostingBlocked}
                onChange={(e) => {
                  setAutoNarration(false);
                  setForm((p) => ({ ...p, notes: e.target.value }));
                }}
                placeholder="Example: Amount paid against bill / Paid supplier bill / Advance adjusted"
              />
            </div>
          </div>

          <div
            style={{
              marginTop: 8,
              display: "flex",
              gap: 14,
              alignItems: "center",
              fontSize: 11,
            }}
          >
            <label style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <input
                type="checkbox"
                checked={autoNarration}
                disabled={isPostingBlocked}
                onChange={(e) => {
                  setAutoNarration(e.target.checked);
                  if (e.target.checked) setForm((p) => ({ ...p, notes: "" }));
                }}
              />
              Auto narration
            </label>

            <span style={{ color: "#dc3545" }}>
              {
                REFERENCE_TYPES.find((x) => x.value === form.referenceType)
                  ?.help
              }
            </span>
          </div>

          <div
            style={{
              marginTop: "12px",
              padding: "10px",
              border: "1px dashed #f1c5bb",
              background: "#fffaf8",
              borderRadius: "8px",
              fontSize: "12px",
            }}
          >
            <div style={{ fontWeight: "700", marginBottom: "5px" }}>
              Journal Preview
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{debitName} Dr</span>
              <b>₹ {money(form.amount)}</b>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>To {creditName}</span>
              <b>₹ {money(form.amount)}</b>
            </div>

            <div style={{ marginTop: 5, color: "#dc3545", fontSize: 11 }}>
              {effectiveNotes}
            </div>
          </div>

          {validationErrors.length > 0 && (
            <div style={{ marginTop: 8, color: "#dc3545", fontSize: 11 }}>
              {validationErrors[0]}
            </div>
          )}

          <div style={{ marginTop: "12px", display: "flex", gap: "8px" }}>
            <button
              onClick={save}
              disabled={
                saving || validationErrors.length > 0 || isPostingBlocked
              }
              style={{
                ...buttonStyle,
                background:
                  saving || validationErrors.length > 0 || isPostingBlocked
                    ? "#dda69c"
                    : "#dc3545",
                color: "#fff",
              }}
            >
              {saving ? "Saving..." : "Save"}
            </button>

            <button
              onClick={clearForm}
              type="button"
              disabled={saving}
              style={{
                ...buttonStyle,
                background: "#6c757d",
                color: "#fff",
              }}
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
